import { useRef, useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { FileTextIcon, TrashIcon, UploadCloudIcon } from '../../../components/icons/Icons';
import type { RfpDocument } from '../../../types/api';

interface UploadQuotationStepProps {
  document: RfpDocument | null;
  uploading: boolean;
  onSelectFile: (file: File) => void;
  onRemove: () => void;
}

const MAX_FILE_MB = 30;

/**
 * Screens RFP 02 (empty drop zone) and RFP 03 (drop zone with an uploaded file).
 */
export function UploadQuotationStep({
  document,
  uploading,
  onSelectFile,
  onRemove,
}: UploadQuotationStepProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const accept = (file: File | undefined) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setLocalError('Only PDF files are supported.');
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setLocalError(`Max file size: ${MAX_FILE_MB} MB`);
      return;
    }
    setLocalError(null);
    onSelectFile(file);
  };

  return (
    <div className="wizard-body">
      <div
        className={`dropzone ${dragging ? 'dropzone--over' : ''}`.trim()}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          accept(event.dataTransfer.files[0]);
        }}
      >
        <div className="dropzone__inner">
          <span className="dropzone__icon">
            <UploadCloudIcon size={24} />
          </span>
          <p className="dropzone__title">Upload Quotation</p>
          <p className="dropzone__hint">
            Drag &amp; drop files here or{' '}
            <button
              type="button"
              className="dropzone__browse"
              onClick={() => inputRef.current?.click()}
            >
              click to Browse
            </button>
          </p>

          <Button
            variant="primary"
            size="lg"
            icon={<FileTextIcon size={16} />}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Choose Files'}
          </Button>

          <p className="dropzone__meta">Only PFD Files are Supported</p>
          <p className="dropzone__meta">Max File Size: {MAX_FILE_MB} MB</p>

          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            className="visually-hidden"
            onChange={(event) => {
              accept(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </div>

        {localError ? <p className="field__error dropzone__section-title">{localError}</p> : null}

        {document ? (
          <>
            <div className="dropzone__divider" />
            <p className="dropzone__section-title">Upload Document</p>
            <div className="file-row">
              <FileTextIcon size={16} className="file-row__icon" />
              <span className="file-row__name" title={document.originalName}>
                {document.originalName}
              </span>
              <span className="text-muted">{document.fileSizeLabel}</span>
              <button
                type="button"
                className="file-row__remove"
                aria-label={`Remove ${document.originalName}`}
                onClick={onRemove}
              >
                <TrashIcon size={16} />
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
